/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
// Standard Library Imports
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';

// External Imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import moment from 'moment';

// Constant Values
const defaultSortDir = 'desc';
const defaultSortProp = 'createTime';

export default class ClassAttendanceAuditController extends Controller {
  @action
  setLimit(value) {
    this.limit = value;
  }

  @action
  setPage(value) {
    this.page = value;
  }

  // Attrs
  // title provides the page title, in use for the header and export file names.
  title = 'Class Attendance Audit Log';

  // queryParams provides a binding to the routes query params.
  queryParams = ['dateFrom', 'dateTo', 'termId'];

  // limitOptions provides the pagination page limit.
  limitOptions = [5, 10, 20, 50, 100];

  // State
  // dateFrom provides the stateful binding to a query param.
  @tracked
  dateFrom = moment().format('YYYY-MM-DD');

  // dateTo provides the stateful binding to a query param.
  @tracked
  dateTo = moment().format('YYYY-MM-DD');

  // limit provides the number of audit logs to show at once.
  @tracked limit = 10;

  // page the page of data to start on for pagination. 1 indexed.
  @tracked page = 1;

  // sortDir provides the current direction of sorting.
  @tracked sortDir = defaultSortDir;

  // sortProp provides the current entity to be used to sort by.
  @tracked sortProp = defaultSortProp;

  // termId provides the stateful binding to a query param.
  @tracked termId = '';

  // Methods
  // selectedTerm returns the currently selected term.
  @computed('termId', 'model.terms.[]')
  get selectedTerm() {
    return this.model.terms.find((item) => item.id === this.termId);
  }

  @tracked
  filters = [];

  // filteredRegisters applies the user's filters to the data array computed above.
  @computed('model.registers.[]', 'filters.@each.{field,value}')
  get filteredRegisters() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.model.registers;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      switch (filter.field) {
        case 'author.heroId':
          dataType = DataType.INTEGER;
          break;
      }

      return {
        key: filter.field,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.model.registers.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  // sortedRegisters sorts the register records by the chosen property and
  // direction.
  @computed('filteredRegisters', 'sortDir', 'sortProp')
  get sortedRegisters() {
    let sortedRegisters = this.filteredRegisters
      .slice()
      .sort((a, b) =>
        a[this.sortProp] > b[this.sortProp]
          ? 1
          : a[this.sortProp] < b[this.sortProp]
            ? -1
            : 0,
      );

    return this.sortDir === 'asc' ? sortedRegisters : sortedRegisters.reverse();
  }

  // pages provides a number of pages based on the sorted data.
  @computed('limit', 'sortedRegisters.[]')
  get pages() {
    let max = Math.max(
      1,
      Math.ceil(this.get('sortedRegisters.length') / this.limit),
    );
    let pages = [];
    for (let i = 1; i <= max; i++) {
      pages.push(i);
    }
    return pages;
  }

  // paginatedRegisters provides an array of page data for display for table
  // pagination.
  @computed('page', 'limit', 'sortProp', 'sortDir', 'sortedRegisters.[]')
  get paginatedRegisters() {
    let ind = (this.page - 1) * this.limit;
    return this.sortedRegisters.slice().splice(ind, this.limit);
  }

  // reset removes all filters.
  reset() {
    this.limit = 10;
    this.page = 1;
    this.filters = [];
    this.sortDir = defaultSortDir;
    this.sortProp = defaultSortProp;
  }

  /**
   * decrementPage moves to the previous page of data.
   */
  @action
  decrementPage() {
    let page = this.page;
    if (page > 1) {
      this.page = page - 1;
    }
  }

  /**
   * incrementPage moves to the next page of data.
   */
  @action
  incrementPage() {
    let page = this.page;
    let max = this.pages.reduce((prev, curr) => (curr > prev ? curr : prev), 0);
    if (page < max) {
      this.page = page + 1;
    }
  }

  /**
   * onChangeDateFrom changes the dateFrom field and removes the currently
   * set term as the date has changed, therefore not explicitly the currently
   * set term.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateFrom(newDate) {
    this.dateFrom = newDate;
    this.termId = '';
  }

  /**
   * onChangeDateTo changes the dateTo field and removes the currently set
   * term as the date has changed.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateTo(newDate) {
    this.dateTo = newDate;
    this.termId = '';
  }

  /**
   * onChangeSort binds in the new parameters for sorting based on what the
   * user chooses.
   *
   * @param {string} sortProp - The field to be sorted by.
   * @param {string} sortDir - The direction to sort in {'asc' | 'desc'}.
   */
  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortDir = sortDir;
    this.sortProp = sortProp;
  }

  /**
   * setSelectedTermId sets the query params, which in turn will refresh the
   * model.
   *
   * @param {Object} term - A term record.
   */
  @action
  setSelectedTermId(term) {
    this.dateFrom = term.start;
    this.dateTo = term.end;
    this.termId = term.id;
  }

  @action
  filter(field, value) {
    let filter = this.filters.find((item) => item.field === field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
